import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { downloadPdf } from "../../lib/pdf";
import { FileText, FileSignature, Plus, Mail, Shield, Package, Eraser, ScanLine, Loader2, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { SignaturePad } from "../../components/SignaturePad";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const STANDARD_FEES = {
  dealer_fee: 199,  // "Dealer Registration Fee" — the only registration line on the BoS
  registration_fee: 0,
  tag_plate_fee: 20,             // Plate
  title_application_fee: 18,     // Title
  mailing_fee: 5,
};

export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/deals").then((r) => setDeals(r.data || []));

  const deleteDeal = async (deal) => {
    const label = `${deal.id.slice(0, 8)} · ${(deal.total || 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}`;
    if (!window.confirm(`Delete deal ${label}? This will remove it from reports and cannot be undone.`)) return;
    try {
      await api.delete(`/deals/${deal.id}`);
      setDeals((ds) => ds.filter((x) => x.id !== deal.id));
      toast.success("Deal deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles((r.data || []).filter((v) => v.status === "available")));
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const dl = async (id, kind) => {
    try {
      await downloadPdf(`${API}/deals/${id}/pdf/${kind}`, `${kind}_${id}.pdf`);
    } catch {
      toast.error("Download failed");
    }
  };

  const dlPacket = async (id) => {
    try {
      await downloadPdf(`${API}/deals/${id}/packet`, `Wakzy_DealPacket_${id.slice(0,8)}.zip`);
      toast.success("Deal packet downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const emailBoS = async (id) => {
    try {
      const { data } = await api.post(`/deals/${id}/email-bill-of-sale`, {});
      if (data.ok) {
        toast.success(`Bill of Sale emailed to ${data.sent_to}`);
        load();
      } else if (data.mocked) {
        toast.warning(`Email service not configured. PDF ready for ${data.sent_to} but RESEND_API_KEY is unset.`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Email failed");
    }
  };

  return (
    <AdminShell title="Deals & Bill of Sale">
      <div className="flex items-center justify-between mb-6">
        <div className="label-eyebrow">{deals.length} deal{deals.length === 1 ? "" : "s"} on file</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta rounded-sm" data-testid="create-deal-btn"><Plus className="w-4 h-4 mr-2" /> New Bill of Sale</Button>
          </DialogTrigger>
          <NewDealDialog
            vehicles={vehicles}
            customers={customers}
            submitting={submitting}
            setSubmitting={setSubmitting}
            onCreated={() => { setOpen(false); load(); }}
          />
        </Dialog>
      </div>

      <div className="surface rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="surface-elev">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Deal ID</th>
              <th className="px-4 py-3 label-eyebrow">Created</th>
              <th className="px-4 py-3 label-eyebrow">Total</th>
              <th className="px-4 py-3 label-eyebrow">Financed</th>
              <th className="px-4 py-3 label-eyebrow">Monthly</th>
              <th className="px-4 py-3 label-eyebrow">Status</th>
              <th className="px-4 py-3 label-eyebrow">Docs</th>
            </tr>
          </thead>
          <tbody>
            {deals.map((d) => (
              <tr key={d.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`deal-row-${d.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-300">
                  <EditableDealRef deal={d} onSaved={(ref) => setDeals((ds) => ds.map((x) => x.id === d.id ? { ...x, deal_reference: ref } : x))} />
                </td>
                <td className="px-4 py-3 text-slate-400">{d.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 gold-text font-semibold">${d.total?.toLocaleString()}</td>
                <td className="px-4 py-3">${d.amount_financed?.toLocaleString()}</td>
                <td className="px-4 py-3">${d.monthly_payment?.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`pill ${d.status === "signed" ? "pill-gold" : "pill-pending"}`}>{d.status}</span>
                </td>
                <td className="px-4 py-3 flex gap-3 flex-wrap">
                  <button onClick={() => dl(d.id, "bill-of-sale")} className="text-[#D4AF37] hover:text-[#f0c659] hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-bos-${d.id}`}>
                    <FileText className="w-3 h-3" /> BoS
                  </button>
                  <button onClick={() => emailBoS(d.id)} className="text-[#D4AF37] hover:text-[#f0c659] hover:underline text-xs inline-flex items-center gap-1" data-testid={`email-bos-${d.id}`} title={d.bos_emailed_at ? `Last emailed ${d.bos_emailed_at.slice(0,10)} to ${d.bos_emailed_to}` : "Email BoS to buyer"}>
                    <Mail className="w-3 h-3" /> {d.bos_emailed_at ? "Resend" : "Email"}
                  </button>
                  <button onClick={() => dl(d.id, "finance-contract")} className="text-[#D4AF37] hover:text-[#f0c659] hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-contract-${d.id}`}>
                    <FileSignature className="w-3 h-3" /> Contract
                  </button>
                  <button onClick={() => dl(d.id, "insurance-letter")} className="text-[#D4AF37] hover:text-[#f0c659] hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-insurance-${d.id}`} title="Insurance Verification Letter — proof of new ownership">
                    <Shield className="w-3 h-3" /> Insurance
                  </button>
                  <button onClick={() => dl(d.id, "sales-invoice")} className="text-[#D4AF37] hover:text-[#f0c659] hover:underline text-xs inline-flex items-center gap-1" data-testid={`download-invoice-${d.id}`} title="Comprehensive sales invoice with billing address & payment mode">
                    <Receipt className="w-3 h-3" /> Invoice
                  </button>
                  <button onClick={() => dlPacket(d.id)} className="text-emerald-400 hover:text-emerald-300 hover:underline text-xs inline-flex items-center gap-1 font-semibold ml-1 pl-2 border-l border-white/10" data-testid={`download-packet-${d.id}`} title="Download all 4 documents as a ZIP">
                    <Package className="w-3 h-3" /> Packet
                  </button>
                  <button
                    onClick={() => deleteDeal(d)}
                    className="text-red-400 hover:text-red-300 hover:underline text-xs inline-flex items-center gap-1 ml-2 pl-2 border-l border-white/10"
                    data-testid={`delete-deal-${d.id}`}
                    title="Delete this deal (admin/manager only)"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {deals.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No deals yet. Customer kiosk activity will appear here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}

function EditableDealRef({ deal, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(deal.deal_reference || "");
  const [saving, setSaving] = useState(false);
  const display = deal.deal_reference || deal.id.slice(0, 8);
  const save = async () => {
    const ref = value.trim();
    if (ref === (deal.deal_reference || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/deals/${deal.id}`, { deal_reference: ref });
      onSaved(ref);
      toast.success(ref ? `Deal ID set to ${ref}` : "Deal ID reset");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setValue(deal.deal_reference || ""); setEditing(false); }
        }}
        disabled={saving}
        placeholder="e.g. WA-2026-0042"
        className="bg-[#04060d] border border-[#D4AF37] rounded-sm h-7 px-2 text-xs font-mono w-32"
        data-testid={`deal-ref-input-${deal.id}`}
      />
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="hover:text-[#D4AF37] cursor-text text-left"
      title={`UUID: ${deal.id}`}
      data-testid={`deal-ref-${deal.id}`}
    >
      {display}
      {!deal.deal_reference && <span className="text-slate-600 ml-1 text-[10px]">✎</span>}
    </button>
  );
}


function NewDealDialog({ vehicles, customers, submitting, setSubmitting, onCreated }) {
  const [form, setForm] = useState({
    vehicle_id: "",
    deal_reference: "",
    customer_id: "",
    customer_inline: { full_name: "", email: "", phone: "", address: "", city: "", state: "GA", zip: "", billing_same: true, billing_address: "", billing_city: "", billing_state: "GA", billing_zip: "", drivers_license: "", license_state: "GA", license_expiry: "", dob: "", insurance_provider: "", insurance_policy: "" },
    use_inline_customer: true,
    sales_price: 0,
    dealer_fee: STANDARD_FEES.dealer_fee,
    registration_fee: STANDARD_FEES.registration_fee,
    tag_plate_fee: STANDARD_FEES.tag_plate_fee,
    title_application_fee: STANDARD_FEES.title_application_fee,
    mailing_fee: STANDARD_FEES.mailing_fee,
    warranty_fee: 0,
    addons: 0,
    trade_in_credit: 0,
    down_payment: 0,
    apr: 8.99,
    term_months: 60,
    payment_method: "financed",
    payment_other: "",
    payment_reference: "",
    sold_as_is: true,
    disclosure_flags: ["sold_as_is"],
    // Scanned document images (uploaded to /documents after deal + customer created)
    scan_dl_image: "",
    scan_ins_image: "",
    odometer_reading: 0,
    representative_name: "",
    representative_signature: "",
    custom_terms: "",
    // Buyers Guide options
    bg_warranty_type: "as_is",
    bg_warranty_scope: "full",
    bg_warranty_percentage: "100",
    bg_warranty_systems: "",
    bg_warranty_duration: "",
    bg_service_contract_available: false,
    bg_customer_name: "",
    bg_customer_signature: "",
  });
  const sigPadRef = useRef(null);
  const bgCustomerSigRef = useRef(null);

  const onVehicle = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    if (v) {
      setForm((f) => ({ ...f, vehicle_id: vid, sales_price: v.price, odometer_reading: v.mileage || 0 }));
    }
  };

  const isCash = form.payment_method === "cash";
  const tavt = Math.round(((Number(form.sales_price) || 0) + (Number(form.dealer_fee) || 0)) * 0.07 * 100) / 100;
  // Cash buyers: dealer never collects DMV fees
  const effReg = isCash ? 0 : Number(form.registration_fee);
  const effTag = isCash ? 0 : Number(form.tag_plate_fee);
  const effTitle = isCash ? 0 : Number(form.title_application_fee);
  const effMail = isCash ? 0 : Number(form.mailing_fee);
  const subtotal = (
    Number(form.sales_price) +
    Number(form.dealer_fee) +
    effReg + effTag + effTitle + effMail +
    Number(form.warranty_fee) +
    Number(form.addons) +
    tavt
  );
  const totalDue = subtotal - Number(form.trade_in_credit) - Number(form.down_payment);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select a vehicle");
    setSubmitting(true);
    try {
      let customer_id = form.customer_id;
      if (form.use_inline_customer || !customer_id) {
        const ci = form.customer_inline;
        if (!ci.full_name || !ci.email) {
          throw new Error("Buyer name and email required");
        }
        // If billing_same is on, blank the billing_* fields (backend treats blank = same as residential)
        const same = ci.billing_same;
        const custPayload = {
          ...ci,
          billing_address: same ? "" : ci.billing_address,
          billing_city: same ? "" : ci.billing_city,
          billing_state: same ? "" : ci.billing_state,
          billing_zip: same ? "" : ci.billing_zip,
        };
        delete custPayload.billing_same;
        const { data: cust } = await api.post("/customers", custPayload);
        customer_id = cust.id;
      }
      const { data: deal } = await api.post("/deals", {
        vehicle_id: form.vehicle_id,
        customer_id,
        deal_reference: (form.deal_reference || "").trim(),
        sales_price: Number(form.sales_price),
        dealer_fee: Number(form.dealer_fee),
        tavt,
        registration_fee: isCash ? 0 : Number(form.registration_fee),
        tag_plate_fee: isCash ? 0 : Number(form.tag_plate_fee),
        title_application_fee: isCash ? 0 : Number(form.title_application_fee),
        mailing_fee: isCash ? 0 : Number(form.mailing_fee),
        warranty_fee: Number(form.warranty_fee),
        addons: Number(form.addons),
        trade_in_credit: Number(form.trade_in_credit),
        down_payment: Number(form.down_payment),
        apr: Number(form.apr),
        term_months: Number(form.term_months),
        payment_method: form.payment_method,
        payment_other: form.payment_other,
        payment_reference: form.payment_reference,
        sold_as_is: (form.disclosure_flags || []).includes("sold_as_is"),
        disclosure_flags: form.disclosure_flags || [],
        odometer_reading: Number(form.odometer_reading),
        representative_name: form.representative_name,
        representative_signature: sigPadRef.current?.getDataURL?.() || "",
        custom_terms: form.custom_terms,
        // Buyers Guide fields
        bg_warranty_type: form.bg_warranty_type,
        bg_warranty_scope: form.bg_warranty_scope,
        bg_warranty_percentage: form.bg_warranty_percentage,
        bg_warranty_systems: form.bg_warranty_systems,
        bg_warranty_duration: form.bg_warranty_duration,
        bg_service_contract_available: form.bg_service_contract_available,
        bg_customer_name: form.bg_customer_name || form.customer_inline?.full_name || "",
        bg_customer_signature: bgCustomerSigRef.current?.getDataURL?.() || "",
        bg_rep_signature: sigPadRef.current?.getDataURL?.() || "",
        bg_signed_at: new Date().toISOString(),
      });
      // Persist any scanned images to /documents for this customer + deal
      const uploadDoc = async (imgB64, docType, filename) => {
        if (!imgB64) return;
        try {
          await api.post("/documents", { customer_id, deal_id: deal.id, doc_type: docType, filename, content_base64: imgB64, mime_type: "image/png" });
        } catch { /* silent — invoice still generates */ }
      };
      await uploadDoc(form.scan_dl_image, "license_front", "license_front.png");
      await uploadDoc(form.scan_ins_image, "insurance", "insurance_card.png");

      toast.success("Bill of Sale created");
      // Trigger PDF download
      window.open(`${API}/deals/${deal.id}/pdf/bill-of-sale`, "_blank");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateBuyer = (k) => (e) => setForm((f) => ({ ...f, customer_inline: { ...f.customer_inline, [k]: e.target.value } }));

  return (
    <DialogContent className="bg-[#0a0e1a] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="chrome-text text-xl">New Motor Vehicle Bill of Sale</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-5" data-testid="bos-form">
        {/* Vehicle + Deal ID */}
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <div className="label-eyebrow mb-2" style={{ color: "var(--wakzy-gold)" }}>Vehicle</div>
            <Select value={form.vehicle_id} onValueChange={onVehicle}>
              <SelectTrigger className="bg-[#04060d] border-white/10 h-11" data-testid="bos-vehicle"><SelectValue placeholder="Select a vehicle" /></SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-white/10 max-h-72">
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} — VIN {v.vin?.slice(-6)} — ${v.price?.toLocaleString()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2" style={{ color: "var(--wakzy-gold)" }}>Deal ID (optional)</div>
            <Input
              value={form.deal_reference}
              onChange={(e) => setForm((f) => ({ ...f, deal_reference: e.target.value }))}
              placeholder="e.g. WA-2026-0042"
              className="bg-[#04060d] border-white/10 h-11 font-mono text-sm"
              data-testid="bos-deal-reference"
            />
            <p className="text-[10px] text-slate-500 mt-1">Leave blank to auto-derive from UUID.</p>
          </div>
        </div>

        {/* Scan documents — OCR auto-fill (drivers license + insurance card) */}
        <div className="surface rounded-md p-4 space-y-3" data-testid="admin-scan-section">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
              <ScanLine className="w-3 h-3 inline mr-1" /> Scan Documents (AI Auto-Fill)
            </div>
            <div className="text-[10px] text-slate-500">Powered by GPT-4o vision</div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <ScanBox
              label="Driver's License"
              testid="scan-dl"
              docType="drivers_license"
              onImage={(dataUrl) => setForm((s) => ({ ...s, scan_dl_image: dataUrl }))}
              onFields={(f) => setForm((s) => ({
                ...s,
                customer_inline: {
                  ...s.customer_inline,
                  full_name: f.full_name || s.customer_inline.full_name,
                  address: f.address || s.customer_inline.address,
                  dob: f.dob || s.customer_inline.dob,
                  drivers_license: f.license_number || s.customer_inline.drivers_license,
                  license_state: f.issue_state || s.customer_inline.license_state,
                  license_expiry: f.expiry_date || s.customer_inline.license_expiry,
                },
              }))}
            />
            <ScanBox
              label="Insurance Card"
              testid="scan-ins"
              docType="insurance_card"
              onImage={(dataUrl) => setForm((s) => ({ ...s, scan_ins_image: dataUrl }))}
              onFields={(f) => setForm((s) => ({
                ...s,
                customer_inline: {
                  ...s.customer_inline,
                  insurance_provider: f.insurance_provider || s.customer_inline.insurance_provider,
                  insurance_policy: f.policy_number || s.customer_inline.insurance_policy,
                },
              }))}
            />
          </div>
          <p className="text-[10px] text-slate-500">
            Upload or snap a photo — fields below will auto-populate. You can still edit them by hand.
          </p>
        </div>

        {/* Buyer */}
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--wakzy-gold)" }}>Buyer Information</div>
          <div className="grid md:grid-cols-2 gap-3">
            <FieldI label="Full Name *" v={form.customer_inline.full_name} on={updateBuyer("full_name")} testid="bos-buyer-name" />
            <FieldI label="Email *" type="email" v={form.customer_inline.email} on={updateBuyer("email")} testid="bos-buyer-email" />
            <FieldI label="Phone" v={form.customer_inline.phone} on={updateBuyer("phone")} testid="bos-buyer-phone" />
            <FieldI label="Driver's License #" v={form.customer_inline.drivers_license} on={updateBuyer("drivers_license")} testid="bos-buyer-dl" />
            <FieldI label="License State" v={form.customer_inline.license_state} on={updateBuyer("license_state")} testid="bos-buyer-license-state" />
            <FieldI label="License Expiry" v={form.customer_inline.license_expiry} on={updateBuyer("license_expiry")} testid="bos-buyer-license-expiry" />
            <FieldI label="Date of Birth" v={form.customer_inline.dob} on={updateBuyer("dob")} testid="bos-buyer-dob" />
            <FieldI label="Address" v={form.customer_inline.address} on={updateBuyer("address")} testid="bos-buyer-addr" />
            <div className="grid grid-cols-3 gap-2">
              <FieldI label="City" v={form.customer_inline.city} on={updateBuyer("city")} />
              <FieldI label="State" v={form.customer_inline.state} on={updateBuyer("state")} />
              <FieldI label="Zip" v={form.customer_inline.zip} on={updateBuyer("zip")} />
            </div>
            <FieldI label="Insurance Provider" v={form.customer_inline.insurance_provider} on={updateBuyer("insurance_provider")} testid="bos-buyer-ins-provider" />
            <FieldI label="Insurance Policy #" v={form.customer_inline.insurance_policy} on={updateBuyer("insurance_policy")} testid="bos-buyer-ins-policy" />
          </div>

          {/* Billing address (if different from residential) */}
          <div className="mt-4 border-t border-white/5 pt-4" data-testid="billing-address-section">
            <div className="flex items-center gap-3 mb-3">
              <input
                id="bos-billing-same"
                type="checkbox"
                checked={!!form.customer_inline.billing_same}
                onChange={(e) => updateBuyer("billing_same")({ target: { value: e.target.checked } })}
                className="w-4 h-4 accent-[#D4AF37]"
                data-testid="bos-billing-same"
              />
              <label htmlFor="bos-billing-same" className="text-sm text-slate-300">
                Billing address <span className="text-slate-500">is the same as residential address</span>
              </label>
            </div>
            {!form.customer_inline.billing_same && (
              <div className="grid md:grid-cols-2 gap-3">
                <FieldI label="Billing Address" v={form.customer_inline.billing_address} on={updateBuyer("billing_address")} testid="bos-billing-addr" />
                <div className="grid grid-cols-3 gap-2">
                  <FieldI label="Billing City" v={form.customer_inline.billing_city} on={updateBuyer("billing_city")} testid="bos-billing-city" />
                  <FieldI label="Billing State" v={form.customer_inline.billing_state} on={updateBuyer("billing_state")} testid="bos-billing-state" />
                  <FieldI label="Billing Zip" v={form.customer_inline.billing_zip} on={updateBuyer("billing_zip")} testid="bos-billing-zip" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment type toggle — visually prominent so staff pick first */}
        <div className="surface rounded-md p-4">
          <div className="label-eyebrow mb-3" style={{ color: "var(--wakzy-gold)" }}>Transaction Type *</div>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, payment_method: "cash" }))}
              className={`p-4 rounded-sm text-left transition border ${form.payment_method === "cash" ? "border-emerald-400 bg-emerald-500/10" : "border-white/10 hover:border-white/30"}`}
              data-testid="txn-type-cash"
            >
              <div className="font-heading text-base text-white mb-1">💵 Cash Purchase</div>
              <div className="text-xs text-slate-400">Buyer handles DMV registration, tag, title, mailing on their own. <span className="text-emerald-400">No DMV fees collected.</span></div>
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, payment_method: "financed" }))}
              className={`p-4 rounded-sm text-left transition border ${form.payment_method !== "cash" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
              data-testid="txn-type-financed"
            >
              <div className="font-heading text-base text-white mb-1">📝 Financed / Other</div>
              <div className="text-xs text-slate-400">Dealer collects full DMV bundle on buyer&apos;s behalf (Reg $199, Tag $18, Title $20, Mailing $5).</div>
            </button>
          </div>
        </div>

        {/* Sale Details */}
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--wakzy-gold)" }}>Sale Details</div>
          <div className="grid md:grid-cols-3 gap-3">
            <FieldN label="Vehicle Sale Price *" v={form.sales_price} on={num("sales_price")} testid="bos-price" />
            <FieldN label="Dealer Processing Fee" v={form.dealer_fee} on={num("dealer_fee")} testid="bos-dealer-fee" />
            <FieldN label="Registration Fee" v={isCash ? 0 : form.registration_fee} on={num("registration_fee")} testid="bos-reg" disabled={isCash} />
            <FieldN label="Tag & Plate Fee" v={isCash ? 0 : form.tag_plate_fee} on={num("tag_plate_fee")} testid="bos-tag" disabled={isCash} />
            <FieldN label="Title Application Fee" v={isCash ? 0 : form.title_application_fee} on={num("title_application_fee")} testid="bos-title" disabled={isCash} />
            <FieldN label="Mailing Fee" v={isCash ? 0 : form.mailing_fee} on={num("mailing_fee")} testid="bos-mail" disabled={isCash} />
            <FieldN label="Warranty Fee" v={form.warranty_fee} on={num("warranty_fee")} />
            <FieldN label="Add-ons" v={form.addons} on={num("addons")} />
            <FieldN label="Odometer Reading (mi)" v={form.odometer_reading} on={num("odometer_reading")} testid="bos-odo" />
            <FieldN label="Trade-in Credit (−)" v={form.trade_in_credit} on={num("trade_in_credit")} />
            <FieldN label="Down Payment (−)" v={form.down_payment} on={num("down_payment")} />
            <div className="surface rounded-sm p-3 text-xs text-slate-300">
              <div className="label-eyebrow mb-1">TAVT (7% auto)</div>
              <div className="font-heading text-base gold-text">${tavt.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wider text-slate-400">Payment Method</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="bos-payment-method-grid">
            {[
              { v: "cash", t: "Cash", icon: "💵" },
              { v: "financed", t: "Financed", icon: "📝" },
              { v: "card", t: "Credit / Debit", icon: "💳" },
              { v: "cashapp", t: "CashApp", icon: "💰" },
              { v: "zelle", t: "Zelle", icon: "⚡" },
              { v: "other", t: "Other", icon: "🔀" },
            ].map((opt) => {
              const active = form.payment_method === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, payment_method: opt.v }))}
                  className={`p-3 rounded-sm text-left transition border flex items-start gap-3 ${
                    active
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`bos-pay-${opt.v}`}
                >
                  <span className={`inline-flex items-center justify-center w-5 h-5 border rounded-[2px] text-[13px] leading-none mt-0.5 shrink-0 ${
                    active ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/40 text-transparent"
                  }`}>{active ? "✓" : ""}</span>
                  <span className="flex-1">
                    <div className="text-lg leading-none">{opt.icon}</div>
                    <div className="font-heading text-sm text-white mt-1">{opt.t}</div>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {form.payment_method === "other" && (
              <FieldI label="Specify Other" v={form.payment_other} on={(e) => setForm((f) => ({ ...f, payment_other: e.target.value }))} testid="bos-pay-other-input" />
            )}
            {["card", "cashapp", "zelle", "other"].includes(form.payment_method) && (
              <FieldI
                label="Payment Reference / Txn ID"
                v={form.payment_reference}
                on={(e) => setForm((f) => ({ ...f, payment_reference: e.target.value }))}
                testid="bos-pay-reference"
              />
            )}
          </div>
        </div>

        {/* Sale Disclosures — mark options */}
        <div className="surface rounded-md p-5 space-y-3" data-testid="sale-disclosure-section">
          <div>
            <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Sale Disclosures</div>
            <p className="text-[11px] text-slate-500 mt-1">
              Every selected item prints on the Bill of Sale, Sales Invoice, and Deal Packet.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="disclosure-grid">
            {[
              { v: "sold_as_is", t: "Sold AS-IS — no dealer warranty" },
              { v: "manufacturer_warranty", t: "Manufacturer warranty still applies" },
              { v: "extended_warranty_offered", t: "Extended service contract offered" },
              { v: "odometer_certified", t: "Odometer certified accurate at delivery" },
              { v: "no_frame_damage", t: "No known frame damage" },
              { v: "no_accident_history", t: "No known reported accident history" },
              { v: "prior_personal", t: "Prior use — Personal" },
              { v: "prior_commercial", t: "Prior use — Commercial" },
              { v: "prior_rental", t: "Prior use — Rental" },
              { v: "prior_fleet", t: "Prior use — Fleet" },
              { v: "prior_taxi", t: "Prior use — Taxi / Livery" },
              { v: "flood_damage_disclosed", t: "Prior flood damage disclosed" },
              { v: "salvage_title", t: "Salvage / rebuilt title disclosed" },
            ].map((opt) => {
              const active = (form.disclosure_flags || []).includes(opt.v);
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() =>
                    setForm((f) => {
                      const flags = new Set(f.disclosure_flags || []);
                      if (flags.has(opt.v)) flags.delete(opt.v);
                      else flags.add(opt.v);
                      return { ...f, disclosure_flags: Array.from(flags), sold_as_is: flags.has("sold_as_is") };
                    })
                  }
                  className={`px-3 py-2 rounded-sm text-xs text-left transition border flex items-center gap-2 ${
                    active
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                      : "border-white/10 hover:border-white/30 text-slate-300"
                  }`}
                  data-testid={`disclosure-${opt.v}`}
                >
                  <span
                    className={`inline-block w-4 h-4 border rounded-[2px] text-center leading-none text-[11px] shrink-0 ${
                      active ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/40"
                    }`}
                  >
                    {active ? "✓" : ""}
                  </span>
                  {opt.t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Representative signature + custom T&Cs */}
        <div className="surface rounded-md p-5 space-y-4">
          <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Seller Representative</div>
          <div className="grid md:grid-cols-2 gap-3">
            <FieldI
              label="Representative Name"
              v={form.representative_name}
              on={(e) => setForm((f) => ({ ...f, representative_name: e.target.value }))}
              testid="bos-rep-name"
            />
            <div className="text-xs text-slate-400 self-end pb-2">
              Printed name appears under signature on the Bill of Sale.
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Representative Signature</Label>
            <SignaturePad
              ref={sigPadRef}
              height={140}
              onChange={(dataURL) => setForm((f) => ({ ...f, representative_signature: dataURL }))}
            />
            <p className="text-[10px] text-slate-500 mt-1">Sign with your finger, stylus, or mouse. Embedded on the BoS in the Seller Representative row.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Additional Terms &amp; Conditions (optional)</Label>
            <Textarea
              value={form.custom_terms}
              onChange={(e) => setForm((f) => ({ ...f, custom_terms: e.target.value }))}
              placeholder="e.g. Buyer to take delivery within 7 days. Inspection waived. Extended warranty offered separately."
              className="bg-[#04060d] border-white/10 mt-1 font-mono text-sm"
              rows={3}
              data-testid="bos-custom-terms"
            />
            <p className="text-[10px] text-slate-500 mt-1">Appended to the standard Terms &amp; Conditions section of the Bill of Sale.</p>
          </div>
        </div>

        {/* FTC Buyers Guide — dealer picks warranty options + dual signatures */}
        <div className="surface rounded-md p-5 space-y-4" data-testid="buyers-guide-section">
          <div>
            <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
              FTC Buyers Guide (16 CFR Part 455)
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Options chosen here are printed as ☒ checkmarks on the bilingual Buyers Guide page of the Bill of Sale.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Warranty Type *</Label>
            <div className="grid md:grid-cols-2 gap-2">
              {[
                { v: "as_is", t: "AS IS — No Dealer Warranty", d: "Vehicle sold in present condition." },
                { v: "dealer_warranty", t: "Dealer Warranty", d: "Dealer covers % of parts & labor." },
                { v: "manufacturer_warranty", t: "Manufacturer / Used Vehicle Warranty", d: "OEM warranty still applies." },
                { v: "implied_only", t: "Implied Warranties Only", d: "No express warranty; state law applies." },
              ].map((opt) => {
                const active = form.bg_warranty_type === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, bg_warranty_type: opt.v }))}
                    className={`p-3 rounded-sm text-left transition border flex items-start gap-3 ${
                      active
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`bg-warranty-${opt.v}`}
                  >
                    <span className={`inline-flex items-center justify-center w-5 h-5 border rounded-[2px] text-[13px] leading-none mt-0.5 shrink-0 ${
                      active ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/40 text-transparent"
                    }`}>{active ? "✓" : ""}</span>
                    <span className="flex-1">
                      <div className="font-heading text-sm text-white">{opt.t}</div>
                      <div className="text-[11px] text-slate-400 mt-1">{opt.d}</div>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {form.bg_warranty_type === "dealer_warranty" && (
            <div className="grid md:grid-cols-2 gap-3 border-l-2 border-[#D4AF37] pl-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Scope</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="bg-warranty-scope-grid">
                  {[
                    { v: "full", t: "FULL Warranty", d: "Covers all listed systems." },
                    { v: "limited", t: "LIMITED Warranty", d: "Covers only specified parts." },
                  ].map((opt) => {
                    const active = form.bg_warranty_scope === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, bg_warranty_scope: opt.v }))}
                        className={`p-3 rounded-sm text-left transition border flex items-start gap-3 ${
                          active
                            ? "border-[#D4AF37] bg-[#D4AF37]/10"
                            : "border-white/10 hover:border-white/30"
                        }`}
                        data-testid={`bg-warranty-scope-${opt.v}`}
                      >
                        <span className={`inline-flex items-center justify-center w-5 h-5 border rounded-[2px] text-[13px] leading-none mt-0.5 shrink-0 ${
                          active ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/40 text-transparent"
                        }`}>{active ? "✓" : ""}</span>
                        <span className="flex-1">
                          <div className="font-heading text-sm text-white">{opt.t}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{opt.d}</div>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <FieldI
                label="Dealer Pays (% of parts & labor)"
                v={form.bg_warranty_percentage}
                on={(e) => setForm((f) => ({ ...f, bg_warranty_percentage: e.target.value }))}
                testid="bg-warranty-pct"
              />
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Systems Covered</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bg-warranty-systems-grid">
                  {[
                    "Engine", "Transmission", "Drive Axles",
                    "Brakes", "Steering", "Suspension",
                    "Electrical", "Air Conditioning",
                  ].map((sys) => {
                    const selected = (form.bg_warranty_systems || "")
                      .split(",").map((s) => s.trim()).filter(Boolean);
                    const active = selected.includes(sys);
                    return (
                      <button
                        key={sys}
                        type="button"
                        onClick={() => {
                          const next = active ? selected.filter((s) => s !== sys) : [...selected, sys];
                          setForm((f) => ({ ...f, bg_warranty_systems: next.join(", ") }));
                        }}
                        className={`px-3 py-2 rounded-sm text-xs text-left transition border flex items-center gap-2 ${
                          active
                            ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                            : "border-white/10 hover:border-white/30 text-slate-300"
                        }`}
                        data-testid={`bg-system-${sys.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span className={`inline-block w-4 h-4 border rounded-[2px] text-center leading-none text-[11px] ${
                          active ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/40"
                        }`}>{active ? "✓" : ""}</span>
                        {sys}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2">
                  <FieldI
                    label="Other systems (comma-separated)"
                    v={(form.bg_warranty_systems || "").split(",").map((s) => s.trim()).filter(Boolean)
                        .filter((s) => !["Engine","Transmission","Drive Axles","Brakes","Steering","Suspension","Electrical","Air Conditioning"].includes(s))
                        .join(", ")}
                    on={(e) => {
                      const preset = (form.bg_warranty_systems || "").split(",").map((s) => s.trim()).filter(Boolean)
                        .filter((s) => ["Engine","Transmission","Drive Axles","Brakes","Steering","Suspension","Electrical","Air Conditioning"].includes(s));
                      const extras = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                      setForm((f) => ({ ...f, bg_warranty_systems: [...preset, ...extras].join(", ") }));
                    }}
                    testid="bg-warranty-systems-other"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <FieldI
                  label="Duration"
                  v={form.bg_warranty_duration}
                  on={(e) => setForm((f) => ({ ...f, bg_warranty_duration: e.target.value }))}
                  testid="bg-warranty-duration"
                />
                <p className="text-[10px] text-slate-500 mt-1">e.g. 30 days or 1,000 miles, whichever comes first.</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <input
              id="bg-service-contract"
              type="checkbox"
              checked={form.bg_service_contract_available}
              onChange={(e) => setForm((f) => ({ ...f, bg_service_contract_available: e.target.checked }))}
              className="w-4 h-4 accent-[#D4AF37]"
              data-testid="bg-service-contract"
            />
            <label htmlFor="bg-service-contract" className="text-sm text-slate-300">
              Service contract available at extra charge on this vehicle
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-white/5">
            <div>
              <FieldI
                label="Buyer Printed Name (for Guide receipt)"
                v={form.bg_customer_name}
                on={(e) => setForm((f) => ({ ...f, bg_customer_name: e.target.value }))}
                testid="bg-customer-name"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Defaults to buyer&apos;s full name from above if left blank.
              </p>
            </div>
            <div className="text-xs text-slate-400 self-end pb-2">
              Both signatures acknowledge delivery of the FTC Buyers Guide.
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">
              Buyer Signature — Buyers Guide Receipt
            </Label>
            <SignaturePad
              ref={bgCustomerSigRef}
              height={140}
              onChange={(dataURL) => setForm((f) => ({ ...f, bg_customer_signature: dataURL }))}
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Printed on the bilingual Buyers Guide acknowledgement row (buyer&apos;s side).
            </p>
          </div>

          <div className="text-[11px] text-slate-500 rounded-sm border border-white/5 bg-white/[0.02] p-3">
            Dealer representative signature above is re-used on the Buyers Guide receipt row.
            Both signatures &amp; the Guide print on the final pages of the Bill of Sale PDF.
          </div>
        </div>

        {/* Totals preview */}
        <div className="gradient-border" style={{ borderRadius: "8px" }}>
          <div className="gradient-border-inner p-4 flex items-center justify-between" style={{ borderRadius: "7px" }}>
            <div>
              <div className="label-eyebrow">Total Purchase Price</div>
              <div className="text-xs text-slate-400 mt-1">Subtotal ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} − credits & down payment</div>
            </div>
            <div className="font-display text-3xl gold-text" data-testid="bos-total">${Math.max(0, totalDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onCreated()}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="btn-cta rounded-sm" data-testid="bos-submit">
            {submitting ? "Creating…" : "Create & Download PDF"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function FieldI({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>
      <Input type={type} value={v} onChange={on} className="bg-[#04060d] border-white/10 h-10 mt-1" data-testid={testid} />
    </div>
  );
}
function FieldN({ label, v, on, testid, disabled }) {
  return (
    <div>
      <Label className={`text-xs uppercase tracking-wider ${disabled ? "text-slate-600" : "text-slate-400"}`}>
        {label}{disabled ? " (cash buyer — N/A)" : ""}
      </Label>
      <Input
        type="number"
        step="0.01"
        value={v}
        onChange={on}
        disabled={disabled}
        className={`bg-[#04060d] border-white/10 h-10 mt-1 font-mono ${disabled ? "opacity-40 cursor-not-allowed line-through" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}


function ScanBox({ label, testid, docType, onFields, onImage }) {
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    setPreview(dataUrl);
    if (onImage) onImage(dataUrl);
    setLoading(true);
    setOk(false);
    try {
      const base64 = dataUrl.split(",")[1];
      const { data } = await api.post("/ocr/extract", { image_base64: base64, doc_type: docType });
      onFields(data.fields || {});
      setOk(true);
      toast.success(`${label} scanned — fields auto-filled`);
    } catch {
      toast.error(`${label} scan failed — image still saved for staff review`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <label
      className={`block border-2 border-dashed rounded-sm p-4 cursor-pointer transition ${
        ok ? "border-emerald-400/50 bg-emerald-500/5"
           : loading ? "border-[#D4AF37]/60 bg-[#D4AF37]/5"
                     : "border-white/15 hover:border-[#D4AF37]/40"
      }`}
      data-testid={testid}
    >
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testid}-input`}
      />
      <div className="flex items-center gap-3">
        {preview ? (
          <img src={preview} alt="" className="w-14 h-14 object-cover rounded-sm border border-white/10" />
        ) : (
          <div className="w-14 h-14 rounded-sm bg-[#04060d] border border-white/10 flex items-center justify-center">
            <ScanLine className="w-6 h-6 text-[#D4AF37]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm text-white">{label}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {loading ? (
              <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Scanning…</span>
            ) : ok ? "Scanned · fields imported"
                  : "Tap to upload or take a photo"}
          </div>
        </div>
      </div>
    </label>
  );
}
